// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useRef, useCallback, useState } from 'react';
import {
  LegacyDecisionTree,
} from '../../lib/tree';
import { DecisionNode } from './nodes/DecisionNode';
import { ChanceNode } from './nodes/ChanceNode';
import { TerminalNode } from './nodes/TerminalNode';
import { useViewport } from './hooks/useViewport';
import { useTreeLayout } from './hooks/useTreeLayout';
import { TreeConnections } from './components/TreeConnections';
import { TerminalBadges } from './components/TerminalBadges';
import { Legend } from './components/Legend';
import type { TreeEdge } from '../../lib/validation';

interface DecisionTreeRendererProps {
  tree: LegacyDecisionTree;
  structureVersion: string; // only changes on topology edits
  selectedNodeId: string | null;
  onNodeSelect: (nodeId: string | null) => void;
  showProbabilities: boolean;
  showCosts: boolean;
  showQALYs: boolean;
  showTransitionDetails?: boolean;
  nodeSize: 'S' | 'M' | 'L';
  issues: Array<{ nodeId?: string }>;
  edges?: TreeEdge[]; // Edge data for transition costs/utilities
}

type CanvasViewProps = {
  tree: LegacyDecisionTree;
  structureVersion: string;
  selectedNodeId: string | null;
  showProbabilities: boolean;
  showCosts: boolean;
  showQALYs: boolean;
  showTransitionDetails?: boolean;
  nodeSize: 'S' | 'M' | 'L';
  onNodeSelect: (nodeId: string | null) => void;
  issues: Array<{ nodeId?: string }>;
  edges?: TreeEdge[];
};

const CanvasView: React.FC<CanvasViewProps> = ({
  tree,
  structureVersion,
  selectedNodeId,
  showProbabilities,
  showCosts,
  showQALYs,
  showTransitionDetails = false,
  nodeSize,
  onNodeSelect,
  issues,
  edges = [],
}) => {
  const bgRef = useRef<SVGRectElement | null>(null);
  
  // Use viewport hook with tree-specific persistence
  const viewport = useViewport({ storageKey: `viewport:${tree.root.id}` });
  
  // Use layout hook
  const { layoutNodes, sizeConfig, dimensions, getNodeData } = useTreeLayout({
    tree,
    structureVersion,
    nodeSize,
  });

  // Label offset utility to avoid edge collisions
  const labelOffset = useCallback((p0: { x: number; y: number }, p1: { x: number; y: number }, k = 10) => {
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len; // left normal
    return { x: (p0.x + p1.x) / 2 + nx * k, y: (p0.y + p1.y) / 2 + ny * k };
  }, []);

  const renderNode = (node: typeof layoutNodes[0]) => {
    const nodeData = getNodeData(node.id);
    if (!nodeData) return null;

    const isSelected = selectedNodeId === node.id;
    const hasIssue = issues.some(issue => issue.nodeId === node.id);
    const isRoot = node.level === 0;

    const nodeProps = {
      node,
      sizeConfig,
      isSelected,
      hasIssue,
      isRoot,
      onSelect: onNodeSelect,
    };

    switch (node.type) {
      case 'decision':
        return <DecisionNode key={node.id} {...nodeProps} />;
      case 'chance':
        return <ChanceNode key={node.id} {...nodeProps} />;
      case 'terminal':
        return <TerminalNode key={node.id} {...nodeProps} />;
      default:
        return null;
    }
  };

  // Panning state
  const isPanning = useRef(false);
  const start = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if ((e.target as Element).closest('[data-clickable]')) return; // ignore node clicks
    isPanning.current = true;
    const current = viewport.get();
    start.current = { x: e.clientX, y: e.clientY, tx: current.tx, ty: current.ty };
    viewport.svgRef.current?.setPointerCapture(e.pointerId);
  }, [viewport]);

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!isPanning.current) return;
    const dx = e.clientX - start.current.x;
    const dy = e.clientY - start.current.y;
    viewport.set({ tx: start.current.tx + dx, ty: start.current.ty + dy });
  }, [viewport]);

  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    isPanning.current = false;
    viewport.svgRef.current?.releasePointerCapture(e.pointerId);
  }, [viewport]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (e.shiftKey) {
      viewport.resetView();
    } else {
      viewport.fitToScreen(dimensions);
    }
  }, [viewport, dimensions]);

  return (
    <div className="renderer-content" style={{ position: 'relative' }}>
      <svg
        ref={viewport.svgRef}
        className="decision-tree-svg graph-root"
        width={dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{ cursor: 'grab', margin: 'auto', display: 'block' }}
        role="graphics-document"
        aria-label="Decision tree"
        tabIndex={0}
      >
        {/* background rect to reliably catch wheel & drags */}
        <rect
          ref={bgRef}
          x={0}
          y={0}
          width="100%"
          height="100%"
          fill="transparent"
          onDoubleClick={handleDoubleClick}
        />
        <defs>
          {/* Arrow marker with non-scaling stroke */}
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path
              d="M0 0 L10 5 L0 10 Z"
              fill="var(--edge-neutral, rgba(255, 255, 255, 0.6))"
              vectorEffect="non-scaling-stroke"
            />
          </marker>

          {/* Focus glow filter for keyboard navigation */}
          <filter id="focusGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="2"
              floodColor="var(--focus)"
              floodOpacity="0.9"
            />
          </filter>

          {/* Subtle grid pattern - reduced contrast by ~35% */}
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path
              d="M 20 0 L 0 0 0 20"
              fill="none"
              stroke="rgba(255,255,255,0.032)"
              strokeWidth="1"
            />
          </pattern>
        </defs>

        {/* Subtle background grid */}
        <rect width="100%" height="100%" fill="url(#grid)" style={{ pointerEvents: 'none' }} />

        {/* Dedicated viewport group - never re-rendered by React */}
        <g ref={viewport.nodeRef} data-viewport>
          <TreeConnections
            layoutNodes={layoutNodes}
            sizeConfig={sizeConfig}
            showProbabilities={showProbabilities}
            showTransitionDetails={showTransitionDetails}
            getNodeData={getNodeData}
            labelOffset={labelOffset}
            edges={edges}
          />
          {layoutNodes.map(renderNode)}
          <TerminalBadges
            layoutNodes={layoutNodes}
            sizeConfig={sizeConfig}
            showCosts={showCosts}
            showQALYs={showQALYs}
            getNodeData={getNodeData}
          />
        </g>
      </svg>
      <Legend />
    </div>
  );
};

const DecisionTreeRenderer: React.FC<DecisionTreeRendererProps> = (props) => {
  const {
    tree,
    structureVersion,
    selectedNodeId,
    onNodeSelect,
    showProbabilities,
    showCosts,
    showQALYs,
    showTransitionDetails = false,
    nodeSize,
    issues,
    edges = [],
  } = props;

  const [rendererExpanded, setRendererExpanded] = useState(true);

  return (
    <div className="decision-tree-renderer">
      <div className="renderer-header">
        <div className="view-toolbar">
        </div>
        <div className="section-header" onClick={() => setRendererExpanded(!rendererExpanded)}>
          <h3>Decision Tree Visualization</h3>
          {/* <span className="collapse-icon">{rendererExpanded ? '▼' : '▶'}</span> */}
        </div>
        <div className="view-toolbar">
          <span className="interaction-hint">Scroll to zoom • Drag to pan</span>
        </div>
      </div>

      {rendererExpanded && (
        <CanvasView
          tree={tree}
          structureVersion={structureVersion}
          selectedNodeId={selectedNodeId}
          showProbabilities={showProbabilities}
          showCosts={showCosts}
          showQALYs={showQALYs}
          showTransitionDetails={showTransitionDetails}
          nodeSize={nodeSize}
          onNodeSelect={onNodeSelect}
          issues={issues}
          edges={edges}
        />
      )}


    </div>
  );
};

export default DecisionTreeRenderer;
